import { startCase } from 'lodash';
import { Address, AddressResult, ConnectionAddressObject } from '../models';
import {
  streetTypeOptions,
  streetTypeSuffixOptions,
  unitTypeOptions,
} from '../AddressManualEntry/options';
import isEmpty from 'lodash/isEmpty';

export const constructDisplayLabel = ({
  allotment,
  unit,
  streetNumber,
  streetName,
  streetType,
  streetTypeSuffix,
  city,
  state,
  postcode,
}: Address): string => {
  const unitTypeAndUnitNumber = unit && unit.split(' ');
  let unitTypeString = '';
  let unitNumberString = '';

  if (unitTypeAndUnitNumber && unitTypeAndUnitNumber.length > 1) {
    unitTypeString = unitTypeAndUnitNumber[0].trim();
    unitNumberString = unitTypeAndUnitNumber[1].trim();
  }

  const unitLabel = getUnitLabel(unitTypeString);
  const streetTypeLabel = getStreetTypeToLabel(streetType);
  const streetTypeSuffixLabel = getStreetTypeSuffixToLabel(streetTypeSuffix);

  const streetLabel = startCase(streetName.toLocaleLowerCase());

  const unitLabelOrType = unitLabel ? unitLabel : unitTypeString ? unitTypeString : '';

  const streetOrLotNumber = streetNumber?.length
    ? streetNumber
    : allotment?.length
    ? `Lot ${allotment},`
    : '';

  const streetMaybeWithType = streetTypeLabel.length
    ? `${streetLabel} ${streetTypeLabel}`
    : `${streetLabel}`;

  const streetMaybeWithTypeAndSuffix = streetTypeSuffixLabel?.length
    ? `${streetMaybeWithType} ${streetTypeSuffixLabel}`
    : `${streetMaybeWithType}`;

  return `${unitLabelOrType} ${
    unitNumberString || ''
  } ${streetOrLotNumber} ${streetMaybeWithTypeAndSuffix}, ${city} ${state} ${postcode}`.trim();
};

export const constructQASLabel = ({
  unitType,
  unitNumber,
  streetNumber,
  streetName,
  city,
  state,
  postcode,
}: Address): string =>
  `${unitType || ''} ${unitNumber || ''} ${streetNumber} ${startCase(
    streetName.toLocaleLowerCase()
  )}, ${city} ${state} ${postcode}`.trim();

export const getOptionLabel = (option?: AddressResult | { label: string }): string =>
  option ? option?.label : '';

export const createSafeLabel = (value: string): string => value.split('').join('\u200b');

export const call = <TArgs extends unknown[]>(
  fn: ((...args: TArgs) => void) | undefined,
  ...params: NoInfer<TArgs>
): void => {
  if (typeof fn === 'function') {
    fn(...params);
  }
};

export const getUnitLabel = (unit: string): string => {
  const units = unitTypeOptions.filter((u) => u.value.toLowerCase() == unit.toLowerCase());
  return units && units.length > 0 ? units[0].label : '';
};

export const getStreetTypeToLabel = (streetType?: string): string => {
  if (!streetType) return '';

  const lowerInput = streetType.toLowerCase();

  const match = streetTypeOptions.find(
    (s) => s.value.toLowerCase() === lowerInput || s.label.toLowerCase() === lowerInput
  );

  return match?.label ?? '';
};

export const getStreetTypeSuffixToLabel = (streetTypeSuffix?: string): string | undefined => {
  const _streetTypeSuffix = streetTypeSuffixOptions.find(
    (s) => s.value === streetTypeSuffix
  )?.label;
  return !isEmpty(_streetTypeSuffix)
    ? _streetTypeSuffix
    : !isEmpty(streetTypeSuffix)
    ? startCase(streetTypeSuffix?.toLowerCase())
    : '';
};

export const stripNMIChecksum = (nmiNumber: string): string => {
  return nmiNumber.length > 10 ? nmiNumber.slice(0, 10) : nmiNumber;
};

export const getAddressLabel = (address: ConnectionAddressObject | undefined) => {
  return (
    address?.addressLabel ||
    address?.qasAddress?.label ||
    (address?.qasAddress && constructDisplayLabel(address.qasAddress)) ||
    ''
  );
};
